// ✅ THEME-ONLY UPDATE: AdminHostelPanel -> match your latest Orange+Blue theme
// - Page BG wrapper + root outlined card
// - Orange gradient header bar
// - Section surface with LEFT orange stripe
// - Table header BLUE (like your latest themed pages)
// - Inputs/Select: blue outline + orange focus ring + orange focused label
// - LinearProgress styled with orange bar
// - Alerts/Snackbar rounded + subtle blue border
// - Keeps ALL logic/APIs intact

import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { API_URL } from "./config";

const AdminHostelPanel = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";

  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const [applications, setApplications] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [Username, setUsername] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [selectedRooms, setSelectedRooms] = useState({});

  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const token = localStorage.getItem("auth");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ===== Theme Sx =====
  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const sectionSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.75 },
  });

  const selectSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
    "& .MuiSelect-select": { py: 1.05 },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const busy = loadingApps || loadingRooms;

  const handleStatusChange = (id, value) => {
    setSelectedStatuses((prev) => ({ ...prev, [id]: value }));
  };

  const handleRoomSelection = (id, value) => {
    setSelectedRooms((prev) => ({ ...prev, [id]: value }));
  };

  // Fetch CampusCode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
      setUsername(decodedToken.username || decodedToken.Username || "");
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Failed to decode authentication token");
    }
  }, [token]);

  const fetchApplications = async () => {
    setError("");
    setLoadingApps(true);
    try {
      const response = await axios.get(`${API_URL}/api/pendingHostelApplications`, {
        ...authHeader,
        // params: { CampusCode: logincampusCode }, // keep if your API needs it
      });
      setApplications(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError("Failed to fetch applications");
    } finally {
      setLoadingApps(false);
    }
  };

  const fetchRooms = async () => {
    setError("");
    setLoadingRooms(true);
    try {
      const response = await axios.get(`${API_URL}/api/availableRooms`, {
        ...authHeader,
        // params: { CampusCode: logincampusCode }, // keep if your API needs it
      });
      setAvailableRooms(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching rooms:", err);
      setError("Failed to fetch available rooms");
    } finally {
      setLoadingRooms(false);
    }
  };

  // Fetch Pending Applications
  useEffect(() => {
    if (!logincampusCode) return;
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode]);

  // Fetch Available Rooms
  useEffect(() => {
    if (!logincampusCode) return;
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode]);

  // Approve/Reject + Assign Room
  const updateApplicationStatus = async (
    id,
    CampusCode,
    StudentKey,
    Block,
    AssignedRoom,
    Status,
    UsernameParam
  ) => {
    try {
      setSavingId(id);
      setError("");

      const finalAssignedRoom =
        selectedRooms[id] ||
        AssignedRoom ||
        applications.find((app) => app.id === id)?.RoomID ||
        "";

      await axios.post(
        `${API_URL}/api/updateHostelApplicationStatus`,
        {
          id,
          CampusCode,
          StudentKey,
          Block,
          AssignedRoom: finalAssignedRoom,
          Status,
          Username: UsernameParam,
        },
        authHeader
      );

      setSuccess(true);

      await fetchApplications();

      setSelectedStatuses((prev) => ({ ...prev, [id]: Status }));
      if (finalAssignedRoom) setSelectedRooms((prev) => ({ ...prev, [id]: finalAssignedRoom }));
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update status");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Hostel Applications Approver
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Review pending requests, assign rooms, and update status
            </Typography>
          </Box>

        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              {error}
            </Alert>
          )}

          <Snackbar open={success} autoHideDuration={2500} onClose={() => setSuccess(false)}>
            <Alert
              severity="success"
              onClose={() => setSuccess(false)}
              sx={{ width: "100%", borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              Operation successful!
            </Alert>
          </Snackbar>

          {(loadingApps || loadingRooms) && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "rgba(25,118,210,0.10)",
                  "& .MuiLinearProgress-bar": { backgroundColor: THEME_ORANGE, borderRadius: 999 },
                }}
              />
            </Box>
          )}

          {/* ✅ Section (left stripe) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Pending Applications
              </Typography>
              <TableContainer sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell sx={{ width: 50 }}>#</TableCell>
                      <TableCell>Sub Campus</TableCell>
                      <TableCell>Student ID</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Block</TableCell>
                      <TableCell>Room Preference</TableCell>
                      <TableCell>RoomType</TableCell>
                      <TableCell>IsAirConditioned</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Occupied</TableCell>
                      <TableCell>Assign Room</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {applications.map((app, index) => (
                      <TableRow key={app.id} hover sx={zebraRowSx(index)}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{app.SubCampusCode}</TableCell>
                        <TableCell>{app.StudentID}</TableCell>
                        <TableCell>{app.FullName}</TableCell>
                        <TableCell>{app.Block}</TableCell>
                        <TableCell>{app.RoomPreference}</TableCell>
                        <TableCell>{app.RoomType}</TableCell>
                        <TableCell>{app.IsAirConditioned}</TableCell>
                        <TableCell>{app.Capacity}</TableCell>
                        <TableCell>{app.Occupied}</TableCell>

                        <TableCell sx={{ minWidth: 280 }}>
                          <Select
                            size="small"
                            sx={selectSx}
                            value={selectedRooms[app.id] || ""}
                            onChange={(e) => handleRoomSelection(app.id, e.target.value)}
                            fullWidth
                          >
                            <MenuItem value="">Select Room</MenuItem>
                            {availableRooms.map((room) => (
                              <MenuItem key={room.RoomID} value={room.RoomID}>
                                {room.Block} {room.RoomNumber} {room.RoomType} (
                                {room.IsAirConditioned === "Yes" ? "AC" : "NonAC"}) (Cap:
                                {room.Capacity}) (Occ:{room.Occupied})
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>

                        <TableCell sx={{ minWidth: 140 }}>
                          <Select
                            size="small"
                            sx={selectSx}
                            value={selectedStatuses[app.id] || app.Status}
                            onChange={(e) => handleStatusChange(app.id, e.target.value)}
                            fullWidth
                          >
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                          </Select>
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            onClick={() =>
                              updateApplicationStatus(
                                app.id,
                                app.CampusCode,
                                app.StudentKey,
                                app.Block,
                                selectedRooms[app.id] || "",
                                selectedStatuses[app.id] || app.Status,
                                Username
                              )
                            }
                            disabled={savingId === app.id}
                          >
                            {savingId === app.id ? (
                              <CircularProgress size={18} sx={{ color: "white" }} />
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {!applications.length && !loadingApps && (
                      <TableRow>
                        <TableCell colSpan={13} sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                          No pending applications.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminHostelPanel;
